/**
 * Portal Brand Context Hook
 *
 * Provides brand context for portal components.
 *
 * - In admin mode (`/admin/portal|reports|reconcile/*`) reads from AdminBrandContext.
 * - In regular mode (`/portal/*`) reads from PortalBrandContext (multi-brand aware).
 * - Falls back to `user.brand` when PortalBrandContext's selection hasn't resolved
 *   (e.g. first render before `myBrands` finishes loading).
 */

import { useLocation } from 'react-router-dom';
import { useAdminBrand } from '../contexts/AdminBrandContext';
import { usePortalBrand } from '../contexts/PortalBrandContext';
import { useCurrentUser } from './useCurrentUser';

interface PortalBrandContext {
  isAdminMode: boolean;
  brandId: number | null;
  snowflakeIdNum: number | null;
  brandName: string | null;
  hasBrandContext: boolean;
  /** True when an admin user is in admin mode and has explicitly selected "All Brands". */
  isAllBrandsView: boolean;
}

export function usePortalBrandContext(): PortalBrandContext {
  const location = useLocation();
  const { user } = useCurrentUser();

  const isAdminMode =
    location.pathname.startsWith('/admin/portal') ||
    location.pathname.startsWith('/admin/reports') ||
    location.pathname.startsWith('/admin/reconcile') ||
    location.pathname.startsWith('/admin/studio');

  let adminBrand: { id: number; displayName: string; snowflakeIdNum?: number } | null = null;
  let adminAllBrands = false;
  try {
    const adminContext = useAdminBrand();
    adminBrand = adminContext.selectedBrand;
    adminAllBrands = adminContext.isAllBrandsView;
  } catch {
    // Not within AdminBrandProvider — normal for non-admin pages.
  }

  // Always call the portal hook so hook order stays stable across renders.
  // (PortalBrandProvider is mounted at the app root, so this never throws.)
  const portal = usePortalBrand();

  if (isAdminMode && adminBrand) {
    const effectiveSnowflakeId = adminBrand.snowflakeIdNum ?? adminBrand.id;
    return {
      isAdminMode: true,
      brandId: adminBrand.id,
      snowflakeIdNum: effectiveSnowflakeId,
      brandName: adminBrand.displayName,
      hasBrandContext: true,
      isAllBrandsView: false,
    };
  }

  // Admin in admin mode with "All Brands" selected (no specific brand).
  if (isAdminMode && adminAllBrands) {
    return {
      isAdminMode: true,
      brandId: null,
      snowflakeIdNum: null,
      brandName: null,
      hasBrandContext: true,
      isAllBrandsView: true,
    };
  }

  // Regular (non-admin) mode — prefer the switcher selection.
  if (portal.selectedBrand) {
    return {
      isAdminMode: false,
      brandId: portal.selectedBrand.id,
      snowflakeIdNum: portal.selectedBrand.snowflakeIdNum,
      brandName: portal.selectedBrand.displayName,
      hasBrandContext: true,
      isAllBrandsView: false,
    };
  }

  // Fallback: use user.brand while PortalBrandContext loads or when user has none.
  if (user?.brand) {
    return {
      isAdminMode: false,
      brandId: user.brand.id,
      // users.me never selected snowflakeIdNum, so this was always null.
      snowflakeIdNum: null,
      brandName: user.brand.displayName,
      hasBrandContext: true,
      isAllBrandsView: false,
    };
  }

  return {
    isAdminMode,
    brandId: null,
    snowflakeIdNum: null,
    brandName: null,
    hasBrandContext: false,
    isAllBrandsView: false,
  };
}

export default usePortalBrandContext;
